import { useState, useEffect } from 'react';
import { ShoppingBag, Check, Loader } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

interface Price {
  id: string;
  unit_amount: number | null;
  currency: string;
  recurring: {
    interval: string;
    interval_count: number;
  } | null;
  active: boolean;
}

interface Product {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  default_price: any;
  prices: Price[];
}

export function AdditionalServices() {
  const { user } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchasingId, setPurchasingId] = useState<string | null>(null);

  useEffect(() => {
    loadProducts();
  }, []);

  async function loadProducts() {
    try {
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-stripe-products`;
      const headers = {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      };

      const response = await fetch(apiUrl, { headers });
      const data = await response.json();

      if (data.products) {
        const additionalProducts = data.products.filter((p: Product) =>
          !p.name.toLowerCase().includes('abonnement') &&
          !p.name.toLowerCase().includes('subscription')
        );
        setProducts(additionalProducts);
      }
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handlePurchase(priceId: string) {
    if (!user) return;

    setPurchasingId(priceId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout-session`;
      const headers = {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      };

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          priceId: priceId,
          userId: user.id,
          userEmail: user.email,
        }),
      });

      const data = await response.json();

      if (data.error) {
        console.error('Checkout error:', data.error);
        alert('Erreur lors de la création de la session de paiement');
        return;
      }

      if (data.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Erreur lors de la création de la session de paiement');
    } finally {
      setPurchasingId(null);
    }
  }

  function formatPrice(price: Price): string {
    if (!price.unit_amount) return 'Gratuit';

    const amount = (price.unit_amount / 100).toFixed(2);
    const currency = price.currency.toUpperCase();

    if (price.recurring) {
      const interval = price.recurring.interval === 'month' ? 'mois' :
                      price.recurring.interval === 'year' ? 'an' : price.recurring.interval;
      return `${amount} ${currency} / ${interval}`;
    }

    return `${amount} ${currency}`;
  }

  if (loading) {
    return (
      <div className="bg-gray-50 rounded-lg p-6">
        <div className="flex items-center justify-center py-8">
          <Loader className="animate-spin text-gray-400" size={24} />
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return null;
  }

  return (
    <div className="bg-gray-50 rounded-lg p-6">
      <div className="flex items-center gap-3 mb-6">
        <ShoppingBag className="text-gray-700" size={24} />
        <h3 className="text-xl font-semibold text-gray-900">Services complémentaires</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map((product) => {
          const activePrice = product.prices.find(p => p.active);
          if (!activePrice) return null;

          return (
            <div
              key={product.id}
              className="bg-white border border-gray-200 rounded-lg p-5 hover:border-gray-300 hover:shadow-md transition-all"
            >
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  {product.name}
                </h4>
                {product.description && (
                  <p className="text-sm text-gray-600 line-clamp-3">
                    {product.description}
                  </p>
                )}
              </div>

              <div className="mb-4">
                <div className="text-2xl font-bold text-gray-900">
                  {formatPrice(activePrice)}
                </div>
              </div>

              <button
                onClick={() => handlePurchase(activePrice.id)}
                disabled={purchasingId === activePrice.id}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {purchasingId === activePrice.id ? (
                  <>
                    <Loader className="animate-spin" size={18} />
                    Redirection...
                  </>
                ) : (
                  <>
                    <Check size={18} />
                    Commander
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
